import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { Navbar, Container, Button, ListGroup, Offcanvas, Form, Badge, Nav, Spinner } from 'react-bootstrap';
import { useTheme } from './utils/ThemeContext';
import AuthPage from './components/AuthPage';
import ChatInterface from './components/ChatInterface';
import DocumentPanel from './components/DocumentPanel';
import DesignBoardPanel from './components/DesignBoardPanel';
import TaskChainPanel from './components/TaskChainPanel';
import ShareDialog from './components/ShareDialog';
import InvitesPanel from './components/InvitesPanel';
import ProfilePage from './components/ProfilePage';
import AccessDeniedPage from './components/AccessDeniedPage';
import AdminPage from './components/AdminPage';
import ErrorBoundary from './components/ErrorBoundary';
import FeedbackDialog from './components/FeedbackDialog';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import ProLandingPage from './components/ProLandingPage';
import AboutPage from './components/AboutPage';
import { setErrorLoggerUid } from './utils/errorLogger';
import { trackEvent, trackPageView, setUserId, trackAdsConversion, trackFunnelEvent } from './utils/analytics';
import {
  subscribeToAuth,
  checkIsAdmin,
  logOut,
  getSessions,
  loadSession,
  loadSessionAsAdmin,
  updateSessionTitle,
  deleteSession,
  saveWorkItems,
  saveDocument,
  saveDecisionItems,
  saveQAItems,
  saveTasks,
  saveScopeOfWork,
  saveScopePhotoSettings,
  saveMoodBoard,
  saveFloorPlan,
  subscribeToSessionState,
  getSharedSessions,
  loadSharedSession,
  leaveSharedSession,
  deleteAllUserData,
  subscribeToSubscription,
  subscribeToUsage,
  toggleFavorite,
  getFavorites,
  removeFavorite,
} from './utils/firebase';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './index.css';

// Valid tab names for URL routing
const VALID_TABS = ['chat', 'design', 'tasks', 'document'];
// Legacy tab names that map to parent tabs (backward compatibility)
const LEGACY_TAB_REDIRECTS = {
  build: 'tasks',
  workitems: 'tasks',
  qa: 'tasks',
  decisions: 'design',
};
const ALL_VALID_TABS = [...VALID_TABS, ...Object.keys(LEGACY_TAB_REDIRECTS)];

// Session View component - handles individual session display
function SessionView({ user, urlSessionId, urlTab, isShared = false, sharedOwnerUid = null, isAdminView = false, adminOwnerUid = null, isAdmin = false, isDevMode = false }) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionTitle, setSessionTitle] = useState(null);
  const [initialMessages, setInitialMessages] = useState([]);
  const [document, setDocument] = useState(null);
  const [todoList, setTodoList] = useState([]);
  const [spaceContext, setSpaceContext] = useState({});
  const [projectEstimate, setProjectEstimate] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [spaceAnalysis, setSpaceAnalysis] = useState([]);
  const [decisionItems, setDecisionItems] = useState([]);
  const [qaItems, setQAItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [scopeOfWork, setScopeOfWork] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [scopePhotoExcluded, setScopePhotoExcluded] = useState([]);
  const [moodBoard, setMoodBoard] = useState([]);
  const [moodBoardGroups, setMoodBoardGroups] = useState([]);
  const [floorPlanData, setFloorPlanData] = useState(null);
  const [activeTab, setActiveTab] = useState(urlTab || 'chat');
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareDialogSession, setShareDialogSession] = useState(null);
  const [sharedSessions, setSharedSessions] = useState([]);
  const [historyTab, setHistoryTab] = useState('my');
  const [favorites, setFavorites] = useState(new Set());
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Shared session tracking
  const [isSharedSession, setIsSharedSession] = useState(isShared);
  // eslint-disable-next-line no-unused-vars
  const [sharedSessionOwnerUid, setSharedSessionOwnerUid] = useState(sharedOwnerUid);
  const [currentRole, setCurrentRole] = useState('owner');

  // Navigate to a session with optional tab
  const navigateToSession = useCallback((id, tab = 'chat') => {
    if (id && !id.startsWith('temp_')) {
      navigate(`/session/${id}/${tab}`);
    }
  }, [navigate]);

  // Update URL when tab changes
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    if (sessionId && !sessionId.startsWith('temp_')) {
      const path = `/session/${sessionId}/${newTab}`;
      navigate(path, { replace: true });
    }
  }, [sessionId, navigate]);

  // Fire a GA page view whenever the active tab changes (or the session
  // changes), so tab navigation within a session shows up as distinct pages.
  // Covers both initial session load (URL or content-based default) and
  // user-driven tab changes via handleTabChange.
  useEffect(() => {
    if (!sessionId || !activeTab) return;
    const isTemp = sessionId.startsWith('temp_');
    const base = isSharedSession && sharedSessionOwnerUid
      ? `/shared/${sharedSessionOwnerUid}/${sessionId}`
      : isTemp
        ? '/session/new'
        : `/session/${sessionId}`;
    trackPageView(`${base}/${activeTab}`);
  }, [sessionId, activeTab, isSharedSession, sharedSessionOwnerUid]);

  // Load session from URL or initialize new session
  useEffect(() => {
    const loadFromUrl = async () => {
      setIsLoading(true);
      setLoadError(null);

      if (!user) {
        setIsLoading(false);
        return;
      }

      // If we have a URL session ID, try to load it
      if (urlSessionId && urlSessionId !== 'new') {
        try {
          let session;
          if (isAdminView && adminOwnerUid) {
            session = await loadSessionAsAdmin(adminOwnerUid, urlSessionId);
            if (session) {
              // Admin viewer is treated as a shared/read-only session in the UI,
              // with role 'admin' which the read-only checks fall through to.
              setIsSharedSession(true);
              setSharedSessionOwnerUid(adminOwnerUid);
              setCurrentRole('admin');
            }
          } else if (isShared && sharedOwnerUid) {
            session = await loadSharedSession(sharedOwnerUid, urlSessionId, user.uid);
            if (session) {
              setIsSharedSession(true);
              setSharedSessionOwnerUid(sharedOwnerUid);
              setCurrentRole(session._role || 'viewer');
            }
          } else {
            session = await loadSession(user.uid, urlSessionId);
            setIsSharedSession(false);
            setSharedSessionOwnerUid(null);
            setCurrentRole('owner');
          }

          if (session) {
            setSessionId(urlSessionId);
            setSessionTitle(session.title || null);
            setInitialMessages(session.messages || []);
            setDocument(session.document ? session.document.content : null);
            setTodoList(session.todoList?.items || []);
            setSpaceContext(session.spaceContext?.data || session.context?.data || {});
            setProjectEstimate(session.projectEstimate?.data || null);
            setWorkItems(session.workItems?.items || []);
            setSpaceAnalysis(session.spaceAnalysis?.items || []);
            setDecisionItems(session.decisionItems?.items || []);
            setQAItems(session.qaItems?.items || []);
            setTasks(session.tasks?.items || []);
            setScopeOfWork(session.scopeOfWork || null);
            setDocuments(Array.isArray(session.documents?.items) ? session.documents.items : []);
            setScopePhotoExcluded(
              Array.isArray(session.scopePhotoSettings?.excluded)
                ? session.scopePhotoSettings.excluded
                : []
            );
            setMoodBoard(session.moodBoard?.items || []);
            setMoodBoardGroups(session.moodBoard?.groups || []);
            setFloorPlanData(session.floorPlan || null);

            // Set active tab from URL or determine from content
            if (urlTab && ALL_VALID_TABS.includes(urlTab)) {
              // Map legacy URLs to current parent tabs
              const mappedTab = LEGACY_TAB_REDIRECTS[urlTab] || urlTab;
              setActiveTab(mappedTab);
            } else {
              // Default based on content
              if (session.document) {
                setActiveTab('document');
                navigate(`/session/${urlSessionId}/document`, { replace: true });
              } else {
                setActiveTab('chat');
                navigate(`/session/${urlSessionId}/chat`, { replace: true });
              }
            }
          } else {
            // Session not found - show error state (don't navigate, let the error UI handle it)
            setLoadError('The session you are looking for does not exist or has been deleted.');
          }
        } catch (error) {
          console.error('Failed to load session:', error);
          setLoadError(error.message || 'Failed to load session. Please try again.');
        }
      } else {
        // No session ID in URL - start new session
        startNewSession();
      }

      setIsLoading(false);
    };

    loadFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId, user?.uid]);

  // Subscribe to real-time session state updates (agent-driven changes)
  const skipFirstStateUpdate = useRef(true);
  useEffect(() => {
    const effectiveUid =
      isAdminView && adminOwnerUid ? adminOwnerUid :
      isShared && sharedOwnerUid ? sharedOwnerUid :
      user?.uid;
    if (!effectiveUid || !sessionId || sessionId.startsWith('temp_')) return;

    skipFirstStateUpdate.current = true;
    const unsubscribe = subscribeToSessionState(effectiveUid, sessionId, (state) => {
      // Skip the first callback — we already loaded this data via loadSession()
      if (skipFirstStateUpdate.current) {
        skipFirstStateUpdate.current = false;
        return;
      }
      setTodoList(state.todoList);
      setSpaceContext(state.spaceContext);
      setProjectEstimate(state.projectEstimate);
      setWorkItems(state.workItems);
      setSpaceAnalysis(state.spaceAnalysis);
      setDecisionItems(state.decisionItems);
      setQAItems(state.qaItems);
      setTasks(Array.isArray(state.tasks) ? state.tasks : []);
      setScopeOfWork(state.scopeOfWork);
      setDocuments(Array.isArray(state.documents) ? state.documents : []);
      const nextExcluded = Array.isArray(state.scopePhotoSettings?.excluded)
        ? state.scopePhotoSettings.excluded
        : [];
      setScopePhotoExcluded(nextExcluded);
      setMoodBoard(state.moodBoard);
      setMoodBoardGroups(state.moodBoardGroups);
      setFloorPlanData(state.floorPlan);

      // Regenerate scope document so before/after images reflect latest
      // design generations and work items from agent-driven updates.
      if (Array.isArray(state.workItems) && state.workItems.length > 0) {
        const docContent = generateDocumentFromWorkItems(
          state.workItems,
          state.spaceContext || {},
          state.spaceAnalysis || [],
          nextExcluded
        );
        setDocument(docContent);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, user?.uid, isShared, sharedOwnerUid, isAdminView, adminOwnerUid]);

  // Handle Stripe checkout return URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'canceled') {
      // Clean the URL params without triggering navigation
      const url = new URL(window.location);
      url.searchParams.delete('checkout');
      window.history.replaceState(null, '', url.pathname);
    }
  }, []);

  // Sync tab from URL changes
  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab, activeTab]);

  const startNewSession = ({ keepSidebarOpen = false } = {}) => {
    if (!user) return;
    const tempId = `temp_${Date.now()}`;
    setSessionId(tempId);
    setSessionTitle(null);
    setInitialMessages([]);
    setDocument(null);
    setTodoList([]);
    setSpaceContext({});
    setWorkItems([]);
    setSpaceAnalysis([]);
    setDecisionItems([]);
    setQAItems([]);
    setTasks([]);
    setScopeOfWork(null);
    setDocuments([]);
    setScopePhotoExcluded([]);
    setMoodBoard([]);
    setActiveTab('chat');
    if (!keepSidebarOpen) {
      setShowHistory(false);
    }
    setIsSharedSession(false);
    setSharedSessionOwnerUid(null);
    setCurrentRole('owner');
    navigate('/session/new/chat', { replace: true });
  };

  const loadPreviousSession = async (id) => {
    if (!user) return;
    setShowHistory(false);
    navigateToSession(id, 'chat');
  };

  const handleDeleteSession = async (e, id) => {
    e.stopPropagation();
    if (!user) return;
    try {
      await deleteSession(user.uid, id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      // Clean up favorite if it was favorited
      if (favorites.has(id)) {
        removeFavorite(user.uid, id).catch(() => {});
        setFavorites((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
      if (id === sessionId) {
        startNewSession({ keepSidebarOpen: true });
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  const handleShowHistory = async () => {
    if (!user) return;
    // Open the drawer immediately so the user gets instant feedback; data loads in
    setShowHistory(true);
    setSessionsLoading(true);
    try {
      const [allSessions, shared, favs] = await Promise.all([
        getSessions(user.uid),
        getSharedSessions(user.uid),
        getFavorites(user.uid),
      ]);
      setSessions(allSessions);
      setSharedSessions(shared);
      setFavorites(favs);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSharedSessionHandler = async (sharedSession) => {
    if (!user) return;
    setShowHistory(false);
    // Navigate to shared session with special route
    navigate(`/shared/${sharedSession.ownerUid}/${sharedSession.sessionId}/chat`);
  };

  const handleInviteAccepted = async () => {
    if (!user) return;
    try {
      const shared = await getSharedSessions(user.uid);
      setSharedSessions(shared);
    } catch (error) {
      console.error('Failed to refresh shared sessions:', error);
    }
  };

  const handleLeaveSharedSession = async (e, sharedSession) => {
    e.stopPropagation();
    if (!user) return;
    try {
      await leaveSharedSession(sharedSession.ownerUid, sharedSession.sessionId, user.uid);
      setSharedSessions((prev) =>
        prev.filter((s) => s.sessionId !== sharedSession.sessionId)
      );
      // Clean up favorite if it was favorited
      if (favorites.has(sharedSession.sessionId)) {
        removeFavorite(user.uid, sharedSession.sessionId).catch(() => {});
        setFavorites((prev) => {
          const next = new Set(prev);
          next.delete(sharedSession.sessionId);
          return next;
        });
      }
      if (sharedSession.sessionId === sessionId) {
        startNewSession({ keepSidebarOpen: true });
      }
    } catch (error) {
      console.error('Failed to leave shared session:', error);
    }
  };

  const handleToggleFavorite = async (e, sessionId) => {
    e.stopPropagation();
    if (!user) return;
    const isFavorited = favorites.has(sessionId);
    try {
      await toggleFavorite(user.uid, sessionId, isFavorited);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (isFavorited) {
          next.delete(sessionId);
        } else {
          next.add(sessionId);
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleDocumentUpdate = (docContent) => {
    setDocument(docContent);
    if (!document) {
      handleTabChange('document');
    }
  };

  const handleTodoUpdate = (items) => {
    setTodoList(items);
  };

  const handleSpaceContextUpdate = (ctx) => {
    setSpaceContext(ctx);
  };

  const handleWorkItemsUpdate = async (items) => {
    setWorkItems(items);

    // Auto-generate scope document from work items (live update)
    if (items.length > 0) {
      const docContent = generateDocumentFromWorkItems(items, spaceContext, spaceAnalysis, scopePhotoExcluded);
      setDocument(docContent);
      // Save document to Firebase
      if (user && sessionId && !sessionId.startsWith('temp_')) {
        try {
          await saveDocument(user.uid, sessionId, docContent);
        } catch (error) {
          console.error('Failed to save document:', error);
        }
      }
    }

    // Save work items to Firebase if we have a real session
    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveWorkItems(user.uid, sessionId, items);
      } catch (error) {
        console.error('Failed to save work items:', error);
      }
    }
  };

  const handleDecisionItemsUpdate = async (items) => {
    setDecisionItems(items);
    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveDecisionItems(user.uid, sessionId, items);
      } catch (error) {
        console.error('Failed to save decision items:', error);
      }
    }
  };

  const handleQAItemsUpdate = async (items) => {
    setQAItems(items);
    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveQAItems(user.uid, sessionId, items);
      } catch (error) {
        console.error('Failed to save QA items:', error);
      }
    }
  };

  const handleTaskToggle = async (taskId, nextStatus) => {
    let updated = null;
    setTasks((prev) => {
      updated = prev.map((t) =>
        t && t.id === taskId
          ? { ...t, status: nextStatus, updatedAt: Date.now() }
          : t
      );
      return updated;
    });
    if (updated && user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveTasks(user.uid, sessionId, updated);
      } catch (error) {
        console.error('Failed to save tasks:', error);
      }
    }
  };

  const handleScopeOfWorkUpdate = async (scope) => {
    setScopeOfWork(scope);
    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveScopeOfWork(user.uid, sessionId, scope);
      } catch (error) {
        console.error('Failed to save scope of work:', error);
      }
    }
  };

  // Toggle a photo's inclusion in the scope-of-work document. Flipping the
  // value regenerates the doc locally for immediate feedback and persists the
  // excluded-URL list to Firebase so the choice survives refreshes.
  const handleScopePhotoToggle = async (url) => {
    if (!url) return;
    const excluded = scopePhotoExcluded.includes(url)
      ? scopePhotoExcluded.filter((u) => u !== url)
      : [...scopePhotoExcluded, url];
    setScopePhotoExcluded(excluded);

    if (workItems.length > 0) {
      const docContent = generateDocumentFromWorkItems(workItems, spaceContext, spaceAnalysis, excluded);
      setDocument(docContent);
      if (user && sessionId && !sessionId.startsWith('temp_')) {
        try {
          await saveDocument(user.uid, sessionId, docContent);
        } catch (error) {
          console.error('Failed to save document:', error);
        }
      }
    }

    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveScopePhotoSettings(user.uid, sessionId, { excluded });
      } catch (error) {
        console.error('Failed to save scope photo settings:', error);
      }
    }
  };

  const handleMoodBoardUpdate = async (items, groups) => {
    setMoodBoard(items);
    if (groups !== undefined) setMoodBoardGroups(groups);
    const groupsToSave = groups !== undefined ? groups : moodBoardGroups;
    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveMoodBoard(user.uid, sessionId, items, groupsToSave);
      } catch (error) {
        console.error('Failed to save mood board:', error);
      }
    }
  };

  const handleFloorPlanUpdate = async (data) => {
    setFloorPlanData(data);
    if (user && sessionId && !sessionId.startsWith('temp_')) {
      try {
        await saveFloorPlan(user.uid, sessionId, data);
      } catch (error) {
        console.error('Failed to save floor plan:', error);
      }
    }
  };

  // Helper to check if a spec is area/measurement related
  const isAreaSpec = (spec) => {
    const label = (spec.label || '').toLowerCase();
    const key = (spec.key || '').toLowerCase();
    const combined = label + ' ' + key;
    return combined.includes('sq') || combined.includes('square') ||
           combined.includes('linear') || combined.includes('feet') ||
           combined.includes('area') || combined.includes('dimension') ||
           combined.includes('size') || combined.includes('length') ||
           combined.includes('width') || combined.includes('height') ||
           combined.includes('measurement');
  };

  // Extract before/after image URLs from space analysis results.
  // Before photos come from 'current_state' analyses; after photos come from
  // 'generated_design' analyses (the AI-rendered visualization).
  const extractBeforeAfterImages = (analyses) => {
    const before = [];
    const after = [];
    (analyses || []).forEach((a) => {
      if (a.analysis_type === 'generated_design') {
        const url = a.image_url || (a.image_data ? `data:${a.mime_type || 'image/png'};base64,${a.image_data}` : null);
        if (url) {
          after.push({ url, caption: a.title || a.design_style || a.prompt || 'Proposed design' });
        }
      } else if (a.analysis_type === 'current_state' && Array.isArray(a.image_urls)) {
        a.image_urls.forEach((url) => {
          before.push({ url, caption: a.room_type || 'Current space' });
        });
      }
    });
    return { before, after };
  };

  // Helper function to generate scope of work document for contractors.
  // `excluded` is an array of photo URLs the user has deselected in the UI.
  const generateDocumentFromWorkItems = (items, ctx, analyses = [], excluded = []) => {
    const projectTitle = ctx.project_title || ctx.project_type || 'Renovation';
    const propertyAddress = ctx.property_address || ctx.location || '';
    const timeline = ctx.timeline || ctx.start_date || '';
    const budget = ctx.budget_range || ctx.budget || '';

    let doc = `# Scope of Work: ${projectTitle}\n\n`;

    // Project Overview
    if (propertyAddress || timeline || budget) {
      doc += `## Project Overview\n\n`;
      if (propertyAddress) doc += `**Property:** ${propertyAddress}\n`;
      if (timeline) doc += `**Timeline:** ${timeline}\n`;
      if (budget) doc += `**Budget Range:** ${budget}\n`;
      doc += '\n';
    }

    // Before & After Photos (filtered by the user's selection)
    const excludedSet = new Set(excluded);
    const { before: allBefore, after: allAfter } = extractBeforeAfterImages(analyses);
    const before = allBefore.filter((img) => !excludedSet.has(img.url));
    const after = allAfter.filter((img) => !excludedSet.has(img.url));
    if (before.length > 0 || after.length > 0) {
      doc += `## Before & After\n\n`;
      if (before.length > 0) {
        doc += `### Before\n\n`;
        before.forEach((img, idx) => {
          const alt = `Before ${idx + 1}${img.caption ? ` - ${img.caption}` : ''}`;
          doc += `![${alt}](${img.url})\n\n`;
          if (img.caption) doc += `*${img.caption}*\n\n`;
        });
      }
      if (after.length > 0) {
        doc += `### After\n\n`;
        after.forEach((img, idx) => {
          const alt = `After ${idx + 1}${img.caption ? ` - ${img.caption}` : ''}`;
          doc += `![${alt}](${img.url})\n\n`;
          if (img.caption) doc += `*${img.caption}*\n\n`;
        });
      }
    }

    // Group items by category
    const groupedItems = items.reduce((acc, item) => {
      const category = item.category || 'General';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    // Scope of Work
    doc += `## Scope of Work\n\n`;

    for (const [category, categoryItems] of Object.entries(groupedItems)) {
      doc += `### ${category}\n\n`;

      for (const item of categoryItems) {
        doc += `#### ${item.title}\n\n`;

        if (item.description) {
          doc += `${item.description}\n\n`;
        }

        // Separate area/measurement specs from other specs
        const specs = item.specifications || [];
        const areaSpecs = specs.filter(s => s.value && isAreaSpec(s));
        const otherSpecs = specs.filter(s => s.value && !isAreaSpec(s));

        // Show area estimates prominently first (important for labor estimates)
        if (areaSpecs.length > 0) {
          doc += `**Estimated Area/Measurements:**\n`;
          for (const spec of areaSpecs) {
            doc += `- **${spec.label}:** ${spec.value}\n`;
          }
          doc += '\n';
        }

        // Add other specifications
        if (otherSpecs.length > 0) {
          doc += `**Specifications:**\n`;
          for (const spec of otherSpecs) {
            doc += `- **${spec.label}:** ${spec.value}\n`;
          }
          doc += '\n';
        }

        if (item.notes) {
          doc += `**Notes:** ${item.notes}\n\n`;
        }

        doc += '---\n\n';
      }
    }

    // Footer
    doc += `*Generated by Plum on ${new Date().toLocaleDateString()}*\n`;

    return doc;
  };

  const handleTitleChange = (title) => {
    setSessionTitle(title);
  };

  const handleTitleEdit = () => {
    setTitleDraft(sessionTitle || '');
    setEditingTitle(true);
  };

  const handleTitleSave = async () => {
    const newTitle = titleDraft.trim();
    if (newTitle && user && sessionId) {
      setSessionTitle(newTitle);
      await updateSessionTitle(user.uid, sessionId, newTitle);
    }
    setEditingTitle(false);
  };

  const handleSessionCreated = (newId) => {
    setSessionId(newId);
    // Update URL without triggering React Router re-render or loadFromUrl effect
    window.history.replaceState(null, '', `/session/${newId}/${activeTab}`);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Build session state object passed to the agent
  const sessionState = {
    todoList,
    spaceContext,
    projectEstimate,
    workItems,
    spaceAnalysis,
    decisionItems,
    qaItems,
    scopeOfWork,
    moodBoard,
    moodBoardGroups,
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  // Show error state when session failed to load
  if (!sessionId && loadError) {
    return (
      <div className="app-wrapper d-flex flex-column vh-100">
        <Navbar variant={resolvedTheme === 'dark' ? 'dark' : 'light'} className="app-navbar">
          <Container fluid>
            <Navbar.Brand className="d-flex align-items-center" role="button" onClick={() => navigate('/home')}>
              <img src="/favicon-32x32.png" alt="Plum" style={{ width: 28, height: 28, marginRight: 8 }} />
              <span className="d-none d-sm-inline">Plum</span>
            </Navbar.Brand>
          </Container>
        </Navbar>
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
          <i className="bi bi-exclamation-circle text-warning" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3">Session Not Found</h4>
          <p className="text-muted">{loadError}</p>
          <Button variant="primary" onClick={() => navigate('/session/new/chat')}>
            Start New Project
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-wrapper d-flex flex-column vh-100">
      <Navbar variant={resolvedTheme === 'dark' ? 'dark' : 'light'} className="app-navbar">
        <Container fluid className="px-2 px-sm-3">
          <Navbar.Brand className="d-flex align-items-center me-2">
            <span role="button" className="d-flex align-items-center" onClick={() => navigate('/home')}>
              <img src="/favicon-32x32.png" alt="Plum" className="me-1 me-sm-2" style={{ width: 28, height: 28 }} />
              <span className="d-none d-sm-inline">Plum</span>
            </span>
            {sessionTitle && !editingTitle && (
              <span
                className="session-title ms-2 ms-sm-3 d-none d-md-inline"
                onClick={handleTitleEdit}
                title="Click to edit title"
              >
                {sessionTitle}
              </span>
            )}
            {editingTitle && (
              <Form
                className="d-none d-md-inline-flex ms-3 align-items-center"
                onSubmit={(e) => { e.preventDefault(); handleTitleSave(); }}
              >
                <Form.Control
                  size="sm"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={handleTitleSave}
                  autoFocus
                  className="title-input"
                />
              </Form>
            )}
          </Navbar.Brand>
          <div className="d-flex align-items-center gap-1 gap-sm-2">
            {/* Share button - only show for owned sessions */}
            {sessionId && !sessionId.startsWith('temp_') && !isSharedSession && (
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => {
                  setShareDialogSession({ id: sessionId, title: sessionTitle });
                  setShowShareDialog(true);
                }}
                title="Share project with collaborators"
                className="d-flex align-items-center"
              >
                <i className="bi bi-people me-1"></i>
                <span className="d-none d-sm-inline">Share</span>
              </Button>
            )}
            {/* Show role badge for shared sessions */}
            {isSharedSession && currentRole !== 'admin' && (
              <Badge bg={currentRole === 'editor' ? 'info' : 'secondary'} className="py-2 d-none d-sm-inline-flex">
                <i className="bi bi-people-fill me-1"></i>
                <span className="d-none d-md-inline">Shared ({currentRole})</span>
              </Badge>
            )}
            {/* Admin viewer badge */}
            {isAdminView && (
              <Badge bg="warning" text="dark" className="py-2 d-none d-sm-inline-flex">
                <i className="bi bi-shield-lock me-1"></i>
                <span className="d-none d-md-inline">Admin view</span>
              </Badge>
            )}
            {/* Projects button - always visible */}
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleShowHistory}
              className="nav-btn"
              title="Projects"
            >
              <i className="bi bi-folder"></i>
              <span className="nav-btn-text ms-1">Projects</span>
            </Button>
            {/* New button - always visible */}
            <Button
              variant="outline-light"
              size="sm"
              onClick={startNewSession}
              className="nav-btn"
              title="New Project"
            >
              <i className="bi bi-plus-lg"></i>
              <span className="nav-btn-text ms-1">New</span>
            </Button>
            {/* Admin dashboard — only visible to admin users */}
            {isAdmin && (
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => navigate('/admin')}
                title="Admin dashboard"
                className="nav-btn"
              >
                <i className="bi bi-shield-lock"></i>
              </Button>
            )}
            {/* Feedback button */}
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setShowFeedback(true)}
              title="Send Feedback"
              className="nav-btn"
            >
              <i className="bi bi-chat-heart"></i>
            </Button>
            {/* Profile button */}
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => navigate('/profile')}
              title="Profile"
              className="nav-btn"
            >
              <i className="bi bi-person-circle"></i>
            </Button>
          </div>
        </Container>
      </Navbar>

      {/* Tab Bar */}
      <div className="tab-bar">
        <button
          className={`tab-item ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => handleTabChange('chat')}
        >
          <i className="bi bi-chat-dots"></i>
          <span>Chat</span>
        </button>
        <button
          className={`tab-item ${activeTab === 'design' ? 'active' : ''}`}
          onClick={() => handleTabChange('design')}
        >
          <i className="bi bi-palette"></i>
          <span>Design</span>
          {decisionItems.filter(d => d.status === 'pending').length > 0 && (
            <span className="tab-badge ms-1"></span>
          )}
        </button>
        <button
          className={`tab-item ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => handleTabChange('tasks')}
        >
          <i className="bi bi-check2-square"></i>
          <span>Tasks</span>
          {tasks.some((t) => t && Array.isArray(t.depends_on) && t.depends_on.length > 0) && (
            <span className="tab-badge ms-1"></span>
          )}
        </button>
        <button
          className={`tab-item ${activeTab === 'document' ? 'active' : ''}`}
          onClick={() => handleTabChange('document')}
        >
          <i className="bi bi-file-earmark-text"></i>
          <span>Documents</span>
        </button>
      </div>

      {/* Tab Content - ChatInterface kept mounted to preserve state */}
      <div className="tab-content-area flex-grow-1 overflow-hidden position-relative">
        <ErrorBoundary>
        {/* ChatInterface - always mounted, handles its own visibility based on activeTab */}
        {sessionId && (
          <ChatInterface
            uid={isAdminView && adminOwnerUid ? adminOwnerUid : user.uid}
            sessionId={sessionId}
            readOnly={isAdminView}
            initialMessages={initialMessages}
            initialTitle={sessionTitle}
            sessionTitle={sessionTitle}
            sessionState={sessionState}
            projectEstimate={projectEstimate}
            onTitleChange={handleTitleChange}
            onDocumentUpdate={handleDocumentUpdate}
            onTodoUpdate={handleTodoUpdate}
            onSpaceContextUpdate={handleSpaceContextUpdate}
            onWorkItemsUpdate={handleWorkItemsUpdate}
            onSpaceAnalysisUpdate={(analysis) => setSpaceAnalysis(analysis)}
            spaceAnalysis={spaceAnalysis}
            onDecisionItemsUpdate={handleDecisionItemsUpdate}
            onQAItemsUpdate={handleQAItemsUpdate}
            onScopeOfWorkUpdate={handleScopeOfWorkUpdate}
            onMoodBoardUpdate={handleMoodBoardUpdate}
            onSessionCreated={handleSessionCreated}
            activeTab={activeTab}
            isAdmin={isAdmin}
            isDevMode={isDevMode}
          />
        )}
        {/* Design tab */}
        <div className={`tab-pane h-100 overflow-auto ${activeTab === 'design' ? 'd-block' : 'd-none'}`}>
          <DesignBoardPanel
            spaceAnalysis={spaceAnalysis}
            messages={initialMessages}
            workItems={workItems}
            moodBoard={moodBoard}
            moodBoardGroups={moodBoardGroups}
            onMoodBoardUpdate={handleMoodBoardUpdate}
            floorPlanData={floorPlanData}
            onFloorPlanUpdate={handleFloorPlanUpdate}
            decisionItems={decisionItems}
            onDecisionItemsChange={handleDecisionItemsUpdate}
            onWorkItemsChange={handleWorkItemsUpdate}
            uid={user.uid}
            sessionId={sessionId}
            sessionTitle={sessionTitle}
            isAdmin={isAdmin}
            isDevMode={isDevMode}
            isActive={activeTab === 'design'}
          />
        </div>
        {/* Tasks tab — grouped to-do list, with checkbox toggling that
            persists back to Firebase. Read-only when viewing as a non-editor. */}
        <div className={`tab-pane h-100 overflow-hidden ${activeTab === 'tasks' ? 'd-block' : 'd-none'}`}>
          <TaskChainPanel
            tasks={tasks}
            onTaskToggle={handleTaskToggle}
            readOnly={isSharedSession && currentRole !== 'editor' && currentRole !== 'owner'}
          />
        </div>
        {/* Documents tab */}
        <div className={`tab-pane h-100 overflow-auto ${activeTab === 'document' ? 'd-block' : 'd-none'}`}>
          <div className="document-tab-content d-flex flex-column h-100">
            <div className="flex-grow-1 overflow-hidden">
              <DocumentPanel
                document={document}
                scopeOfWork={scopeOfWork}
                documents={documents}
                messages={initialMessages}
                scopePhotos={(() => {
                  const { before, after } = extractBeforeAfterImages(spaceAnalysis);
                  return [
                    ...before.map((img) => ({ ...img, kind: 'before' })),
                    ...after.map((img) => ({ ...img, kind: 'after' })),
                  ];
                })()}
                scopePhotoExcluded={scopePhotoExcluded}
                onScopePhotoToggle={handleScopePhotoToggle}
                canEditScopePhotos={!isSharedSession || currentRole === 'editor' || currentRole === 'owner'}
                uid={user.uid}
                isActive={activeTab === 'document'}
              />
            </div>
          </div>
        </div>
        </ErrorBoundary>
      </div>

      {/* Session history drawer */}
      <Offcanvas show={showHistory} onHide={() => setShowHistory(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Projects</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          {/* Invites Panel */}
          <InvitesPanel user={user} onInviteAccepted={handleInviteAccepted} />

          {/* Tabs for My Projects, Favorites, and Shared With Me */}
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link
                active={historyTab === 'my'}
                onClick={() => setHistoryTab('my')}
              >
                My Projects
                {sessions.length > 0 && (
                  <Badge bg="secondary" className="ms-1">{sessions.length}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={historyTab === 'favorites'}
                onClick={() => setHistoryTab('favorites')}
              >
                <i className="bi bi-star-fill me-1" style={{ fontSize: '0.75rem' }}></i>
                Favorites
                {favorites.size > 0 && (
                  <Badge bg="warning" text="dark" className="ms-1">{favorites.size}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={historyTab === 'shared'}
                onClick={() => setHistoryTab('shared')}
              >
                Shared With Me
                {sharedSessions.length > 0 && (
                  <Badge bg="info" className="ms-1">{sharedSessions.length}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Loading indicator while fetching projects */}
          {sessionsLoading && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading projects...</span>
              </Spinner>
            </div>
          )}

          {/* My Projects Tab */}
          {!sessionsLoading && historyTab === 'my' && (
            <>
              {sessions.length === 0 ? (
                <p className="text-muted">No projects found. Start a new consultation!</p>
              ) : (
                <ListGroup variant="flush">
                  {sessions.map((session) => (
                    <ListGroup.Item
                      key={session.id}
                      action
                      active={session.id === sessionId && !isSharedSession}
                      onClick={() => loadPreviousSession(session.id)}
                      className="d-flex justify-content-between align-items-start"
                    >
                      <div className="text-truncate me-2">
                        <div className="fw-medium text-truncate">
                          {session.title || 'Untitled Session'}
                        </div>
                        <small className="text-muted">
                          {formatDate(session.createdAt)}
                          {session.document && (
                            <span className="badge bg-success ms-2">Doc</span>
                          )}
                          {session.collaborators && (
                            <span className="badge bg-info ms-2" title={`Shared with ${Object.keys(session.collaborators).length}`}>
                              <i className="bi bi-people-fill me-1"></i>
                              {Object.keys(session.collaborators).length}
                            </span>
                          )}
                        </small>
                      </div>
                      <div className="d-flex align-items-center flex-shrink-0">
                        <Button
                          variant="link"
                          size="sm"
                          className="share-btn-drawer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareDialogSession({ id: session.id, title: session.title || 'Untitled Session' });
                            setShowShareDialog(true);
                          }}
                          title="Share project"
                        >
                          <i className="bi bi-person-plus"></i>
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className={`favorite-btn${favorites.has(session.id) ? ' active' : ''}`}
                          onClick={(e) => handleToggleFavorite(e, session.id)}
                          title={favorites.has(session.id) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <i className={`bi ${favorites.has(session.id) ? 'bi-star-fill' : 'bi-star'}`}></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ms-1 flex-shrink-0"
                          onClick={(e) => handleDeleteSession(e, session.id)}
                          title="Delete session"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </>
          )}

          {/* Favorites Tab */}
          {!sessionsLoading && historyTab === 'favorites' && (() => {
            const favoriteMySessions = sessions.filter((s) => favorites.has(s.id));
            const favoriteSharedSessions = sharedSessions.filter((s) => favorites.has(s.sessionId));
            const hasFavorites = favoriteMySessions.length > 0 || favoriteSharedSessions.length > 0;
            return (
              <>
                {!hasFavorites ? (
                  <p className="text-muted">No favorite projects yet. Star a project to add it here.</p>
                ) : (
                  <ListGroup variant="flush">
                    {favoriteMySessions.map((session) => (
                      <ListGroup.Item
                        key={session.id}
                        action
                        active={session.id === sessionId && !isSharedSession}
                        onClick={() => loadPreviousSession(session.id)}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <div>
                          <div className="fw-medium">
                            {session.title || 'Untitled Session'}
                          </div>
                          <small className="text-muted">
                            {formatDate(session.createdAt)}
                            {session.document && (
                              <span className="badge bg-success ms-2">Doc</span>
                            )}
                          </small>
                        </div>
                        <div className="d-flex align-items-center flex-shrink-0">
                          <Button
                            variant="link"
                            size="sm"
                            className="share-btn-drawer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareDialogSession({ id: session.id, title: session.title || 'Untitled Session' });
                              setShowShareDialog(true);
                            }}
                            title="Share project"
                          >
                            <i className="bi bi-person-plus"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="ms-2 flex-shrink-0 favorite-btn active"
                            onClick={(e) => handleToggleFavorite(e, session.id)}
                            title="Remove from favorites"
                          >
                            <i className="bi bi-star-fill"></i>
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                    {favoriteSharedSessions.map((sharedSession) => (
                      <ListGroup.Item
                        key={sharedSession.sessionId}
                        action
                        active={sharedSession.sessionId === sessionId && isSharedSession}
                        onClick={() => loadSharedSessionHandler(sharedSession)}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <div>
                          <div className="fw-medium">
                            {sharedSession.sessionTitle || 'Untitled Project'}
                          </div>
                          <small className="text-muted">
                            From {sharedSession.ownerEmail}
                            <Badge
                              bg={sharedSession.role === 'editor' ? 'primary' : 'secondary'}
                              className="ms-2"
                            >
                              {sharedSession.role}
                            </Badge>
                          </small>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="ms-2 flex-shrink-0 favorite-btn active"
                          onClick={(e) => handleToggleFavorite(e, sharedSession.sessionId)}
                          title="Remove from favorites"
                        >
                          <i className="bi bi-star-fill"></i>
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </>
            );
          })()}

          {/* Shared With Me Tab */}
          {!sessionsLoading && historyTab === 'shared' && (
            <>
              {sharedSessions.length === 0 ? (
                <p className="text-muted">No projects have been shared with you yet.</p>
              ) : (
                <ListGroup variant="flush">
                  {sharedSessions.map((sharedSession) => (
                    <ListGroup.Item
                      key={sharedSession.sessionId}
                      action
                      active={sharedSession.sessionId === sessionId && isSharedSession}
                      onClick={() => loadSharedSessionHandler(sharedSession)}
                      className="d-flex justify-content-between align-items-start"
                    >
                      <div>
                        <div className="fw-medium">
                          {sharedSession.sessionTitle || 'Untitled Project'}
                        </div>
                        <small className="text-muted">
                          From {sharedSession.ownerEmail}
                          <Badge
                            bg={sharedSession.role === 'editor' ? 'primary' : 'secondary'}
                            className="ms-2"
                          >
                            {sharedSession.role}
                          </Badge>
                        </small>
                      </div>
                      <div className="d-flex align-items-center flex-shrink-0">
                        <Button
                          variant="link"
                          size="sm"
                          className={`favorite-btn${favorites.has(sharedSession.sessionId) ? ' active' : ''}`}
                          onClick={(e) => handleToggleFavorite(e, sharedSession.sessionId)}
                          title={favorites.has(sharedSession.sessionId) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <i className={`bi ${favorites.has(sharedSession.sessionId) ? 'bi-star-fill' : 'bi-star'}`}></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="ms-1 flex-shrink-0"
                          onClick={(e) => handleLeaveSharedSession(e, sharedSession)}
                          title="Leave project"
                        >
                          <i className="bi bi-box-arrow-left"></i>
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Share Dialog */}
      <ShareDialog
        show={showShareDialog}
        onHide={() => {
          setShowShareDialog(false);
          setShareDialogSession(null);
        }}
        session={shareDialogSession || { id: sessionId, title: sessionTitle }}
        user={user}
      />

      {/* Feedback Dialog */}
      <FeedbackDialog
        show={showFeedback}
        onHide={() => setShowFeedback(false)}
        user={user}
      />
    </div>
  );
}

// Wrapper component to extract URL params
function SessionRoute({ user, isAdmin, isDevMode }) {
  const { sessionId, tab } = useParams();
  return (
    <SessionView
      user={user}
      urlSessionId={sessionId}
      urlTab={tab}
      isAdmin={isAdmin}
      isDevMode={isDevMode}
    />
  );
}

// Profile route wrapper — renders ProfilePage at /profile so refresh/back works
function ProfileRoute({ user }) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const [subscription, setSubscription] = useState({ tier: 'free' });
  const [usage, setUsage] = useState({});

  useEffect(() => {
    if (!user?.uid) return;
    const unsubSub = subscribeToSubscription(user.uid, setSubscription);
    const unsubUsage = subscribeToUsage(user.uid, setUsage);
    return () => { unsubSub(); unsubUsage(); };
  }, [user?.uid]);

  const handleSignOut = async () => {
    await logOut();
    navigate('/');
  };

  const handleDeleteAllData = async () => {
    if (!user) return;
    await deleteAllUserData(user.uid, user.email);
    navigate('/');
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="app-wrapper d-flex flex-column vh-100">
      <Navbar variant={resolvedTheme === 'dark' ? 'dark' : 'light'} className="app-navbar">
        <Container fluid>
          <Navbar.Brand className="d-flex align-items-center" role="button" onClick={() => navigate('/home')}>
            <img src="/favicon-32x32.png" alt="Plum" style={{ width: 28, height: 28, marginRight: 8 }} />
            <span className="d-none d-sm-inline">Plum</span>
          </Navbar.Brand>
        </Container>
      </Navbar>
      <div className="flex-grow-1 overflow-hidden">
        <ProfilePage
          user={user}
          subscription={subscription}
          usage={usage}
          onSignOut={handleSignOut}
          onDeleteAllData={handleDeleteAllData}
          onBack={handleBack}
        />
      </div>
    </div>
  );
}

// Shared session route wrapper
function SharedSessionRoute({ user, isAdmin, isDevMode }) {
  const { ownerUid, sessionId, tab } = useParams();
  return (
    <SessionView
      user={user}
      urlSessionId={sessionId}
      urlTab={tab}
      isShared={true}
      sharedOwnerUid={ownerUid}
      isAdmin={isAdmin}
      isDevMode={isDevMode}
    />
  );
}

// Admin session route — lets admins open any user's session in read-only mode
// by URL. RTDB rules enforce that only admins can actually read the data, so
// non-admins who hit this URL just see the "Session Not Found" error state.
function AdminSessionRoute({ user, isAdmin, isDevMode }) {
  const { ownerUid, sessionId, tab } = useParams();
  return (
    <SessionView
      user={user}
      urlSessionId={sessionId}
      urlTab={tab}
      isAdminView={true}
      adminOwnerUid={ownerUid}
      isAdmin={isAdmin}
      isDevMode={isDevMode}
    />
  );
}

// Main App component - handles auth and routing
function App() {
  const [user, setUser] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const location = useLocation();

  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  // Track the last uid we fired `logged_in` for, so an auth callback that
  // re-fires for the same user (token refresh, profile mutation) doesn't
  // double-count. Sign-out resets this to null so a subsequent sign-in fires
  // a fresh funnel step.
  const lastLoggedInUidRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeToAuth(async (firebaseUser) => {
      setUser(firebaseUser || null);
      setErrorLoggerUid(firebaseUser ? firebaseUser.uid : null);
      setUserId(firebaseUser ? firebaseUser.uid : null);

      if (!firebaseUser) {
        lastLoggedInUidRef.current = null;
      }

      if (firebaseUser) {
        // Distinguish a brand-new account from a returning sign-in by comparing
        // Firebase's creation and last sign-in timestamps. New users have
        // matching (or near-matching) values.
        const created = Number(firebaseUser.metadata?.createdAt) ||
          (firebaseUser.metadata?.creationTime ? Date.parse(firebaseUser.metadata.creationTime) : 0);
        const lastSignIn = Number(firebaseUser.metadata?.lastLoginAt) ||
          (firebaseUser.metadata?.lastSignInTime ? Date.parse(firebaseUser.metadata.lastSignInTime) : 0);
        const isNewUser = created && lastSignIn && Math.abs(lastSignIn - created) < 60_000;
        const providerId = firebaseUser.providerData?.[0]?.providerId || 'unknown';
        const method = providerId.includes('google') ? 'google'
          : providerId.includes('password') ? 'email'
            : providerId;
        trackEvent(isNewUser ? 'sign_up' : 'sign_in', { method });
        if (isNewUser) {
          trackAdsConversion('sign_up');
        }
        // Funnel step 2: any successful auth state — net-new sign-up or
        // resumed session — once per uid per tab.
        if (lastLoggedInUidRef.current !== firebaseUser.uid) {
          lastLoggedInUidRef.current = firebaseUser.uid;
          trackFunnelEvent('logged_in', { method, is_new_user: !!isNewUser });
        }
      }

      if (firebaseUser && isDevMode) {
        const admin = await checkIsAdmin(firebaseUser.uid);
        setIsAdmin(admin);
      }
      setAdminCheckComplete(true);
    });
    return unsubscribe;
  }, [isDevMode]);

  // Watch subscription tier and cancellation transitions so we capture paid
  // signups, cancellations, and reactivations regardless of which UI path the
  // user took (Stripe checkout, customer portal, webhook backfill).
  const lastSubRef = useRef(null);
  useEffect(() => {
    if (!user?.uid) {
      lastSubRef.current = null;
      return;
    }
    const unsub = subscribeToSubscription(user.uid, (data) => {
      const prev = lastSubRef.current;
      const next = data || { tier: 'free' };
      const prevTier = prev?.tier || 'free';
      const nextTier = next.tier || 'free';
      const prevCancel = !!(prev?.cancelAtPeriodEnd || prev?.cancelAt);
      const nextCancel = !!(next.cancelAtPeriodEnd || next.cancelAt);

      // Skip the first emission (initial state, not a real transition).
      if (prev !== null) {
        if (prevTier === 'free' && nextTier !== 'free') {
          trackEvent('subscription_started', { tier: nextTier, status: next.status || null });
          trackAdsConversion('purchase');
          trackAdsConversion('subscribe');
          // Funnel step 10: completed paid upgrade. Driven off the
          // subscription state transition (not the Stripe redirect return)
          // so it fires regardless of which checkout path the user took.
          trackFunnelEvent('subscription_form_submitted', {
            tier: nextTier,
            status: next.status || null,
          });
        } else if (prevTier !== 'free' && nextTier !== 'free' && prevTier !== nextTier) {
          trackEvent('subscription_changed', { from_tier: prevTier, to_tier: nextTier });
        } else if (prevTier !== 'free' && nextTier === 'free') {
          trackEvent('subscription_ended', { from_tier: prevTier });
        }

        if (!prevCancel && nextCancel) {
          trackEvent('subscription_canceled', { tier: nextTier, status: next.status || null });
        } else if (prevCancel && !nextCancel && nextTier !== 'free') {
          trackEvent('subscription_resumed', { tier: nextTier });
        }
      }
      lastSubRef.current = next;
    });
    return unsub;
  }, [user?.uid]);

  // Public routes accessible without authentication
  if (location.pathname === '/terms') return <TermsPage />;
  if (location.pathname === '/privacy') return <PrivacyPage />;
  if (location.pathname === '/about') return <AboutPage />;
  if (location.pathname === '/home') return <AuthPage user={user} />;
  if (location.pathname === '/pro' || location.pathname === '/pro/') return <ProLandingPage />;
  if (location.pathname === '/blog' || location.pathname === '/blog/') return <BlogPage />;
  if (location.pathname.startsWith('/blog/')) {
    const slug = location.pathname.slice('/blog/'.length).replace(/\/+$/, '');
    return <BlogPostPage slug={decodeURIComponent(slug)} />;
  }

  // Show loading spinner while checking auth or admin status
  if (user === undefined || (isDevMode && user && !adminCheckComplete)) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  // Show auth page if not logged in
  if (!user) {
    return <AuthPage />;
  }

  // Show access denied if dev mode and not admin
  if (isDevMode && !isAdmin) {
    return <AccessDeniedPage />;
  }

  return (
    <Routes>
      {/* Session routes */}
      <Route path="/session/:sessionId/:tab" element={<SessionRoute user={user} isAdmin={isAdmin} isDevMode={isDevMode} />} />
      <Route path="/session/:sessionId" element={<SessionRoute user={user} isAdmin={isAdmin} isDevMode={isDevMode} />} />

      {/* Shared session routes */}
      <Route path="/shared/:ownerUid/:sessionId/:tab" element={<SharedSessionRoute user={user} isAdmin={isAdmin} isDevMode={isDevMode} />} />
      <Route path="/shared/:ownerUid/:sessionId" element={<SharedSessionRoute user={user} isAdmin={isAdmin} isDevMode={isDevMode} />} />

      {/* Admin session view — access is gated by RTDB rules (admins-only reads) */}
      <Route path="/admin/user/:ownerUid/session/:sessionId/:tab" element={<AdminSessionRoute user={user} isAdmin={isAdmin} isDevMode={isDevMode} />} />
      <Route path="/admin/user/:ownerUid/session/:sessionId" element={<AdminSessionRoute user={user} isAdmin={isAdmin} isDevMode={isDevMode} />} />

      {/* Admin dashboard — listing of users, sessions, and error reports.
          The /admin route is only mounted when isAdmin is true; otherwise the
          catch-all redirects to home. RTDB rules also enforce admin-only reads. */}
      {isAdmin && (
        <Route path="/admin" element={<AdminPage />} />
      )}

      {/* Profile route */}
      <Route path="/profile" element={<ProfileRoute user={user} />} />

      {/* Home route - redirect to new session */}
      <Route path="/" element={<Navigate to="/session/new/chat" replace />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
